import { useEffect, useState } from 'react';
import { findLatestContractIdByQuoteId } from '@/features/contract/state/savedContractsIdb';
import { loadLatestSavedQuoteByDraftKey } from './state/savedQuotesIdb';

/**
 * PR-C1 (ADR-0035) Phase 1 — forward-chain «Вперёд» КП → Договор.
 *
 * Async-проверка наличия downstream-договора для текущего КП. Логика живёт в
 * контейнере-слое (хук), не в презентационной секции `BottomActionButtons`
 * (ADR-0005). Возвращает готовый nav-path `/contracts/<id>/edit` либо null.
 *
 * **Разрешение id текущего КП** (для связи с договором через `fromQuoteId`):
 *   1. если открыли по `?savedQuoteId=<uuid>` (ADR-0032 restore) → это id;
 *   2. иначе latest saved quote по `draftKey` → его id;
 *   3. иначе (КП ещё ни разу не сохранён, только draft) → id нет → null.
 *
 * Затем `findLatestContractIdByQuoteId(quoteId)` → последний договор по
 * `updatedAt` (решение #2). Нет договора → null → кнопка скрыта (решение #1).
 *
 * Чистое чтение IDB (R2). graceful: ошибки → null (кнопка скрыта).
 * Переиспользуем существующий persistent-маршрут `/contracts/:id/edit` (R3 —
 * никаких новых URL-параметров).
 */
export function useForwardToContract(
  draftKey: string,
  savedQuoteIdParam: string | null,
): string | null {
  const [contractEditPath, setContractEditPath] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolve(): Promise<void> {
      try {
        let quoteId = savedQuoteIdParam;
        if (!quoteId) {
          const saved = await loadLatestSavedQuoteByDraftKey(draftKey);
          if (cancelled) return;
          quoteId = saved?.id ?? null;
        }
        if (!quoteId) {
          if (!cancelled) setContractEditPath(null);
          return;
        }
        const contractId = await findLatestContractIdByQuoteId(quoteId);
        if (cancelled) return;
        setContractEditPath(contractId ? `/contracts/${contractId}/edit` : null);
      } catch {
        // graceful: IDB недоступен → кнопка скрыта
        if (!cancelled) setContractEditPath(null);
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [draftKey, savedQuoteIdParam]);

  return contractEditPath;
}
